import type { SiteRole } from '~/types/practice'

export const STAFF_CAPABILITY_KEYS = [
  'booking.view',
  'booking.manage',
  'booking.schedule',
  'reception.view',
  'reception.manage',
  'visit.view',
  'visit.manage',
  'health.view',
  'health.manage',
  'health.config',
] as const

export type StaffCapability = (typeof STAFF_CAPABILITY_KEYS)[number]
export type StaffPermissions = Record<StaffCapability, boolean>
export type StaffRole = Exclude<SiteRole, 'owner'>

const PARENT: Partial<Record<StaffCapability, StaffCapability>> = {
  'booking.manage': 'booking.view',
  'booking.schedule': 'booking.view',
  'reception.manage': 'reception.view',
  'visit.manage': 'visit.view',
  'health.manage': 'health.view',
  'health.config': 'health.view',
}

export const PERMISSION_GROUPS: Array<{
  id: string
  label: string
  items: Array<{ key: StaffCapability; label: string }>
}> = [
  {
    id: 'booking',
    label: 'نوبت‌دهی',
    items: [
      { key: 'booking.view', label: 'مشاهده نوبت‌ها' },
      { key: 'booking.manage', label: 'ثبت، ویرایش و حذف نوبت' },
      { key: 'booking.schedule', label: 'تغییر شیفت و تقویم' },
    ],
  },
  {
    id: 'reception',
    label: 'لیست بیماران',
    items: [
      { key: 'reception.view', label: 'مشاهده بیماران' },
      { key: 'reception.manage', label: 'ثبت و ویرایش بیمار' },
    ],
  },
  {
    id: 'visit',
    label: 'ویزیت مجازی',
    items: [
      { key: 'visit.view', label: 'مشاهده ویزیت‌ها' },
      { key: 'visit.manage', label: 'شروع، پایان و ارسال لینک' },
    ],
  },
  {
    id: 'health',
    label: 'سوابق سلامت',
    items: [
      { key: 'health.view', label: 'مشاهده سابقه' },
      { key: 'health.manage', label: 'ثبت و ویرایش شرح حال' },
      { key: 'health.config', label: 'فرم‌ها و ساخت سابقه' },
    ],
  },
]

function blankPermissions(): StaffPermissions {
  return Object.fromEntries(STAFF_CAPABILITY_KEYS.map((key) => [key, false])) as StaffPermissions
}

export function defaultStaffPermissions(role: StaffRole): StaffPermissions {
  const permissions = blankPermissions()
  if (role === 'secretary') {
    permissions['booking.view'] = true
    permissions['booking.manage'] = true
    permissions['reception.view'] = true
    permissions['reception.manage'] = true
    return permissions
  }
  permissions['booking.view'] = true
  permissions['booking.manage'] = true
  permissions['visit.view'] = true
  permissions['visit.manage'] = true
  permissions['health.view'] = true
  permissions['health.manage'] = true
  return permissions
}

export function isStaffCapability(value: string): value is StaffCapability {
  return (STAFF_CAPABILITY_KEYS as readonly string[]).includes(value)
}

/** Empty stored JSON keeps the role default. Explicit booleans override it, and a child switch turns its parent on. */
export function resolveStaffPermissions(role: StaffRole, raw: unknown): StaffPermissions {
  const permissions = defaultStaffPermissions(role)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return permissions
  const input = raw as Record<string, unknown>
  const explicit = STAFF_CAPABILITY_KEYS.filter((key) => typeof input[key] === 'boolean')
  if (!explicit.length) return permissions
  for (const key of explicit) permissions[key] = input[key] as boolean
  for (const key of STAFF_CAPABILITY_KEYS) {
    const parent = PARENT[key]
    if (permissions[key] && parent) permissions[parent] = true
  }
  for (const key of STAFF_CAPABILITY_KEYS) {
    const parent = PARENT[key]
    if (parent && !permissions[parent]) permissions[key] = false
  }
  return permissions
}

export function toggleStaffPermission(
  current: StaffPermissions,
  key: StaffCapability,
  on: boolean,
): StaffPermissions {
  const next = { ...current, [key]: on }
  if (on) {
    const parent = PARENT[key]
    if (parent) next[parent] = true
  }
  else {
    for (const child of STAFF_CAPABILITY_KEYS) {
      if (PARENT[child] === key) next[child] = false
    }
  }
  return next
}
